import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Card,
  CardMedia,
  CardActions,
  List,
  ListItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  AutoAwesome as AutoAwesomeIcon,
  PostAdd as PostAddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../contexts/AdminContext';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { Card as CardType } from '../types';
import { wrapEbayUrl } from '../utils/ebayTracking';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  isUploading?: boolean;
  uploadProgress?: number;
}

const AdminCardManager: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const { mode } = useAppTheme();
  const [cards, setCards] = useState<CardType[]>([]);
  const [filteredCards, setFilteredCards] = useState<CardType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCard, setEditingCard] = useState<CardType | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<ImageFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingImageUrls, setEditingImageUrls] = useState<string[]>(['']);
  const [newCardImageUrls, setNewCardImageUrls] = useState<string[]>(['']);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newCard, setNewCard] = useState<Partial<CardType>>({
    player: '',
    team: '',
    year: new Date().getFullYear(),
    brand: '',
    price: 0,
    sport: 'basketball',
    condition: 'Mint',
    isGraded: false,
    gradingCompany: '',
    grade: '',
    description: '',
    ebayUrl: '',
    isHot: false,
    isPosted: false,
    isHomepage: false,
    imageUrl: '',
  });
  const [newCardImages, setNewCardImages] = useState<ImageFile[]>([]);
  const [isNewCardDragOver, setIsNewCardDragOver] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<CardType | null>(null);
  const [postOptions, setPostOptions] = useState({
    postToCollection: true,
    postToHomepage: false,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Determine colors based on current theme
  const isLight = mode === 'light';
  const cardBg = isLight ? '#FFFFFF' : 'rgba(21, 30, 47, 0.5)';
  const borderColor = isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)';
  const textColor = isLight ? '#000000' : '#FFFFFF';

  // Dropdown options
  const yearOptions = Array.from({ length: 46 }, (_, i) => 2025 - i).reverse(); // 1980-2025
  const sportOptions = ['basketball', 'football', 'baseball', 'hockey', 'soccer'];
  const brandOptions = [
    'Panini', 'Topps', 'Upper Deck', 'Fleer', 'Donruss', 'Leaf', 'Score', 
    'Skybox', 'Pinnacle', 'Stadium Club', 'Bowman', 'Chrome', 'Prizm',
    'Select', 'Optic', 'Mosaic', 'Contenders', 'National Treasures'
  ];
  const conditionOptions = [
    'Mint', 'Near Mint', 'Excellent', 'Very Good', 'Good', 'Fair', 'Poor'
  ];
  const gradingCompanyOptions = [
    'PSA', 'BGS', 'SGC', 'CSG', 'HGA', 'CGC', 'ISA', 'GMA', 'KSA', 'Raw'
  ];
  const gradeOptions = [
    '10', '9.5', '9', '8.5', '8', '7.5', '7', '6.5', '6', '5.5', '5', 
    '4.5', '4', '3.5', '3', '2.5', '2', '1.5', '1'
  ];

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin-yyd92c');
      return;
    }

    // Load cards from localStorage only
    const savedCards = localStorage.getItem('adminCards');
    if (savedCards) {
      try {
        const parsedCards = JSON.parse(savedCards);
        setCards(parsedCards);
        setFilteredCards(parsedCards);
      } catch (error) {
        console.error('Error loading saved cards:', error);
        setCards([]);
        setFilteredCards([]);
      }
    } else {
      // Start with empty array if no saved cards
      setCards([]);
      setFilteredCards([]);
    }
  }, [isAdmin, navigate]);

  // Image upload handlers
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const newImages: ImageFile[] = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      isUploading: false,
      uploadProgress: 0,
    }));

    setUploadedImages(prev => [...prev, ...newImages]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set drag over to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  const removeImage = useCallback((imageId: string) => {
    setUploadedImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  const simulateImageUpload = useCallback(async (image: ImageFile): Promise<string> => {
    // Simulate upload progress
    const updatedImage = { ...image, isUploading: true, uploadProgress: 0 };
    setUploadedImages(prev => prev.map(img => img.id === image.id ? updatedImage : img));

    // Simulate upload progress
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(resolve => setTimeout(resolve, 100));
      setUploadedImages(prev => prev.map(img => 
        img.id === image.id 
          ? { ...img, uploadProgress: i }
          : img
      ));
    }

    // Convert blob URL to data URL for persistence
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        
        // Mark as uploaded
        setUploadedImages(prev => prev.map(img => 
          img.id === image.id 
            ? { ...img, isUploading: false, uploadProgress: 100 }
            : img
        ));
        
        resolve(dataUrl);
      };
      
      img.src = image.preview;
    });
  }, []);

  const uploadAllImages = useCallback(async () => {
    const unuploadedImages = uploadedImages.filter(img => !img.isUploading && img.uploadProgress !== 100);
    for (const image of unuploadedImages) {
      await simulateImageUpload(image);
    }
  }, [uploadedImages, simulateImageUpload]);

  // New card handlers
  const handleAddCard = () => {
    setIsAddDialogOpen(true);
    setNewCard({
      player: '',
      team: '',
      year: new Date().getFullYear(),
      brand: '',
      price: 0,
      sport: 'basketball',
      condition: 'Mint',
      isGraded: false,
      gradingCompany: '',
      grade: '',
      description: '',
      ebayUrl: '',
      isHot: false,
      isPosted: false,
      isHomepage: false,
      imageUrl: '',
    });
    setNewCardImages([]);
    setNewCardImageUrls(['']);
  };

  const handleCancelAdd = () => {
    setIsAddDialogOpen(false);
    setNewCard({
      player: '',
      team: '',
      year: new Date().getFullYear(),
      brand: '',
      price: 0,
      sport: 'basketball',
      condition: 'Mint',
      isGraded: false,
      gradingCompany: '',
      grade: '',
      description: '',
      ebayUrl: '',
      isHot: false,
      isPosted: false,
      isHomepage: false,
      imageUrl: '',
    });
    setNewCardImages([]);
    setIsNewCardDragOver(false);
    setNewCardImageUrls(['']);
  };

  const handleNewCardFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const newImages: ImageFile[] = Array.from(files).map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      isUploading: false,
      uploadProgress: 0,
    }));

    setNewCardImages(prev => [...prev, ...newImages]);
  }, []);

  const handleNewCardDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNewCardDragOver(true);
  }, []);

  const handleNewCardDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNewCardDragOver(true);
  }, []);

  const handleNewCardDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsNewCardDragOver(false);
    }
  }, []);

  const handleNewCardDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNewCardDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleNewCardFileSelect(files);
    }
  }, [handleNewCardFileSelect]);

  const removeNewCardImage = useCallback((imageId: string) => {
    setNewCardImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  const generateAIDescription = useCallback(async () => {
    if (!newCard.player || !newCard.team || !newCard.brand || !newCard.year) {
      setSnackbar({
        open: true,
        message: 'Please fill in player, team, brand, and year before generating description',
        severity: 'error',
      });
      return;
    }

    setIsGeneratingDescription(true);
    
    try {
      // Simulate AI description generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const gradeText = newCard.isGraded && newCard.gradingCompany && newCard.grade 
        ? `, ${newCard.gradingCompany} ${newCard.grade} graded`
        : '';
      
      const conditionText = newCard.condition ? ` in ${newCard.condition} condition` : '';
      const priceText = newCard.price ? ` with a current market value of $${newCard.price.toLocaleString()}` : '';
      
      const aiDescription = `A premium ${newCard.year} ${newCard.brand} ${newCard.player} ${newCard.team} sports card${gradeText}${conditionText}. This collectible piece showcases ${newCard.player}'s excellence and represents a valuable addition to any sports card collection${priceText}. Perfect for collectors and investors seeking high-quality memorabilia.`;
      
      setNewCard(prev => ({ ...prev, description: aiDescription }));
      
      setSnackbar({
        open: true,
        message: 'AI description generated successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error generating AI description. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsGeneratingDescription(false);
    }
  }, [newCard]);

  const handleSaveNewCard = async () => {
    if (!newCard.player || !newCard.team || !newCard.brand || !newCard.year || (newCard.price || 0) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields: Player, Team, Brand, Year, and Price',
        severity: 'error',
      });
      return;
    }

    try {
      // Upload images first
      const unuploadedImages = newCardImages.filter(img => !img.isUploading && img.uploadProgress !== 100);
      for (const image of unuploadedImages) {
        await simulateImageUpload(image);
      }
      
      // Get the primary image URL
      const primaryImageUrl = newCardImages.length > 0 
        ? newCardImages[0].preview 
        : newCard.imageUrl || '';

      // Create new card
      const cardToAdd: CardType = {
        id: Math.random().toString(36).substr(2, 9),
        title: `${newCard.year} ${newCard.brand} ${newCard.player} ${newCard.team}`,
        player: newCard.player!,
        team: newCard.team!,
        year: newCard.year!,
        brand: newCard.brand!,
        price: newCard.price || 0,
        sport: newCard.sport!,
        condition: newCard.condition!,
        isGraded: newCard.isGraded!,
        gradingCompany: newCard.gradingCompany,
        grade: newCard.grade,
        description: newCard.description || '',
        ebayUrl: newCard.ebayUrl ? wrapEbayUrl(newCard.ebayUrl) : '',
        imageUrl: primaryImageUrl,
        isHot: newCard.isHot || false,
        isPosted: false,
        isHomepage: newCard.isHomepage || false,
        priceChange: 0,
        // Store additional image URLs from the URL fields (skip the first one as it's the primary)
        additionalImageUrls: newCardImageUrls.slice(1).filter(url => url.trim() !== ''),
      };

      // Add to cards list
      const updatedCards = [...cards, cardToAdd];
      setCards(updatedCards);
      setFilteredCards(updatedCards);
      
      // Save to localStorage
      localStorage.setItem('adminCards', JSON.stringify(updatedCards));

      setSnackbar({
        open: true,
        message: 'Card saved successfully!',
        severity: 'success',
      });

      handleCancelAdd();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error saving card. Please try again.',
        severity: 'error',
      });
    }
  };

  const handlePostListing = () => {
    if (!newCard.player || !newCard.team || !newCard.brand || !newCard.year || (newCard.price || 0) <= 0) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields before posting',
        severity: 'error',
      });
      return;
    }

    // Reset post options
    setPostOptions({
      postToCollection: true,
      postToHomepage: false,
    });
    setShowPostDialog(true);
  };

  const confirmPostListing = async () => {
    try {
      // Upload images first
      const unuploadedImages = newCardImages.filter(img => !img.isUploading && img.uploadProgress !== 100);
      for (const image of unuploadedImages) {
        await simulateImageUpload(image);
      }
      
      // Get the primary image URL
      const primaryImageUrl = newCardImages.length > 0 
        ? newCardImages[0].preview 
        : newCard.imageUrl || '';

      // Create new card with posting options
      const cardToAdd: CardType = {
        id: Math.random().toString(36).substr(2, 9),
        title: `${newCard.year} ${newCard.brand} ${newCard.player} ${newCard.team}`,
        player: newCard.player!,
        team: newCard.team!,
        year: newCard.year!,
        brand: newCard.brand!,
        price: newCard.price || 0,
        sport: newCard.sport!,
        condition: newCard.condition!,
        isGraded: newCard.isGraded!,
        gradingCompany: newCard.gradingCompany,
        grade: newCard.grade,
        description: newCard.description || '',
        ebayUrl: newCard.ebayUrl ? wrapEbayUrl(newCard.ebayUrl) : '',
        imageUrl: primaryImageUrl,
        isHot: newCard.isHot || false,
        isPosted: postOptions.postToCollection,
        isHomepage: postOptions.postToHomepage,
        priceChange: 0,
        // Store additional image URLs from the URL fields (skip the first one as it's the primary)
        additionalImageUrls: newCardImageUrls.slice(1).filter(url => url.trim() !== ''),
      };

      // Add to cards list
      const updatedCards = [...cards, cardToAdd];
      setCards(updatedCards);
      setFilteredCards(updatedCards);
      
      // Save to localStorage
      localStorage.setItem('adminCards', JSON.stringify(updatedCards));

      // Show success message based on what was posted
      let message = '';
      if (postOptions.postToCollection && postOptions.postToHomepage) {
        message = 'Card posted to Goat Collection and Homepage successfully!';
      } else if (postOptions.postToCollection) {
        message = 'Card posted to Goat Collection successfully!';
      } else if (postOptions.postToHomepage) {
        message = 'Card posted to Homepage successfully!';
      }

      setSnackbar({
        open: true,
        message,
        severity: 'success',
      });

      setShowPostDialog(false);
      handleCancelAdd();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error posting card. Please try again.',
        severity: 'error',
      });
    }
  };

  const cancelPostListing = () => {
    setShowPostDialog(false);
  };

  // Existing handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setFilteredCards(cards);
    } else {
    const filtered = cards.filter(card => 
        card.player.toLowerCase().includes(query.toLowerCase()) ||
        card.team.toLowerCase().includes(query.toLowerCase()) ||
        card.brand.toLowerCase().includes(query.toLowerCase()) ||
      card.year.toString().includes(query) ||
        card.sport.toLowerCase().includes(query.toLowerCase()) ||
        (card.grade && card.grade.includes(query)) ||
        card.condition.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCards(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredCards(cards);
  };

  const handleClearAllCards = () => {
    setShowClearAllDialog(true);
  };

  const confirmClearAllCards = () => {
    setCards([]);
    setFilteredCards([]);
    localStorage.removeItem('adminCards');
    
    setSnackbar({
      open: true,
      message: 'All cards cleared successfully!',
      severity: 'success',
    });
    setShowClearAllDialog(false);
  };

  const cancelClearAllCards = () => {
    setShowClearAllDialog(false);
  };

  const handleEditCard = (card: CardType) => {
    setEditingCard(card);
    setIsEditDialogOpen(true);
    // Initialize uploaded images from existing image URL
    if (card.imageUrl) {
      setUploadedImages([{
        id: 'existing',
        file: new File([], 'existing-image'),
        preview: card.imageUrl,
        uploadProgress: 100,
      }]);
    } else {
      setUploadedImages([]);
    }

    // Initialize image URLs array with existing URLs
    const allUrls = [card.imageUrl || ''];
    if (card.additionalImageUrls && card.additionalImageUrls.length > 0) {
      allUrls.push(...card.additionalImageUrls);
    }
    setEditingImageUrls(allUrls);
  };

  const handleCancelEdit = () => {
    setEditingCard(null);
    setIsEditDialogOpen(false);
    setUploadedImages([]);
    setIsDragOver(false);
    setEditingImageUrls(['']);
  };

  const handleSaveCard = async () => {
    if (!editingCard) return;

    try {
      // Upload images first
      await uploadAllImages();
      
      // Get the primary image URL (first uploaded image or existing)
      const primaryImageUrl = uploadedImages.length > 0 
        ? uploadedImages[0].preview 
        : editingCard.imageUrl;

      // Update the card with new image URLs
      const updatedCard = {
        ...editingCard,
        imageUrl: primaryImageUrl,
        ebayUrl: editingCard.ebayUrl ? wrapEbayUrl(editingCard.ebayUrl) : '',
        // Store all image URLs for future use
        additionalImages: uploadedImages.slice(1).map(img => img.preview),
        // Store additional image URLs from the URL fields (skip the first one as it's the primary)
        additionalImageUrls: editingImageUrls.slice(1).filter(url => url.trim() !== ''),
      };

      // Update cards list
      const updatedCards = cards.map(card => 
        card.id === updatedCard.id ? updatedCard : card
      );
      setCards(updatedCards);
      setFilteredCards(updatedCards);
      
      // Save to localStorage
      localStorage.setItem('adminCards', JSON.stringify(updatedCards));

    setSnackbar({
      open: true,
      message: 'Card updated successfully!',
      severity: 'success',
    });

      handleCancelEdit();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error updating card. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleDeleteCard = (card: CardType) => {
    setCardToDelete(card);
    setShowDeleteDialog(true);
  };

  const confirmDeleteCard = () => {
    if (!cardToDelete) return;
    
    const updatedCards = cards.filter(card => card.id !== cardToDelete.id);
    setCards(updatedCards);
    setFilteredCards(updatedCards);
    
    // Save to localStorage
    localStorage.setItem('adminCards', JSON.stringify(updatedCards));
    
    setSnackbar({
      open: true,
      message: `"${cardToDelete.player}" card deleted successfully!`,
      severity: 'success',
    });
    setShowDeleteDialog(false);
    setCardToDelete(null);
  };

  const cancelDeleteCard = () => {
    setShowDeleteDialog(false);
    setCardToDelete(null);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };


  const handleEditingImageUrlChange = (index: number, url: string) => {
    setEditingImageUrls(prev => {
      const newUrls = [...prev];
      newUrls[index] = url;
      return newUrls;
    });
    
    // Update the main imageUrl if this is the first URL field
    if (index === 0 && editingCard) {
      setEditingCard({ ...editingCard, imageUrl: url });
    }
  };

  const addEditingImageUrl = () => {
    setEditingImageUrls(prev => [...prev, '']);
  };

  const removeEditingImageUrl = (index: number) => {
    setEditingImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleNewCardImageUrlChange = (index: number, url: string) => {
    setNewCardImageUrls(prev => {
      const newUrls = [...prev];
      newUrls[index] = url;
      return newUrls;
    });
    
    // Update the main imageUrl if this is the first URL field
    if (index === 0) {
      setNewCard(prev => ({ ...prev, imageUrl: url }));
    }
  };

  const addNewCardImageUrl = () => {
    setNewCardImageUrls(prev => [...prev, '']);
  };

  const removeNewCardImageUrl = (index: number) => {
    setNewCardImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: textColor }}>
          Card Manager
        </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your sports card inventory and listings
        </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleClearAllCards}
              sx={{
                px: 2,
                py: 1.5,
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              Clear All Cards
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCard}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Add Card
            </Button>
          </Box>
        </Box>
        
        <Paper sx={{ p: 3, backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
            <TextField
              fullWidth
            label="Search Cards"
              placeholder="Search cards by player, team, brand, year, sport, grade, or condition..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      size="small"
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            {searchQuery && (
              <Chip 
                label={`${filteredCards.length} of ${cards.length} cards`}
                color="primary"
                variant="outlined"
              />
            )}
        </Paper>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4, backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: textColor }}>Image</TableCell>
              <TableCell sx={{ color: textColor }}>Player</TableCell>
              <TableCell sx={{ color: textColor }}>Team</TableCell>
              <TableCell sx={{ color: textColor }}>Year</TableCell>
              <TableCell sx={{ color: textColor }}>Brand</TableCell>
              <TableCell sx={{ color: textColor }}>Price</TableCell>
              <TableCell sx={{ color: textColor }}>Graded</TableCell>
              <TableCell sx={{ color: textColor }}>Status</TableCell>
              <TableCell sx={{ color: textColor }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCards.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchQuery ? 'No cards found matching your search.' : 'No cards available.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCards.map((card) => (
                <TableRow key={card.id} hover>
                  <TableCell>
                    <Box
                      component="img"
                      src={card.imageUrl}
                      alt={card.player}
                      sx={{
                        width: 60,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: textColor }}>
                      {card.player}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: textColor }}>{card.team}</TableCell>
                  <TableCell sx={{ color: textColor }}>{card.year}</TableCell>
                  <TableCell sx={{ color: textColor }}>{card.brand}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                      color: mode === 'light' ? '#000000' : '#FFFFFF'
                    }}>
                      {formatPrice(card.price)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={card.isGraded ? 'Graded' : 'Raw'}
                      color={card.isGraded ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {card.isPosted && (
                        <Chip
                          label="Posted"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {card.isHomepage && (
                        <Chip
                          label="Homepage"
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {!card.isPosted && !card.isHomepage && (
                        <Chip
                          label="Draft"
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit Card">
                      <IconButton
                        onClick={() => handleEditCard(card)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Card">
                      <IconButton
                        onClick={() => handleDeleteCard(card)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Enhanced Edit Card Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon />
            Edit Card: {editingCard?.player}
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingCard && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                {/* Image Upload Section */}
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{ mb: 2, color: textColor }}>
                    Images ({uploadedImages.length})
                  </Typography>
                  
                  {/* Drag & Drop Upload Area */}
                  <Box
                      sx={{
                      border: `2px dashed ${isDragOver ? 'primary.main' : borderColor}`,
                        borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                      transition: 'all 0.2s ease',
                        mb: 2,
                      cursor: 'pointer',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input')?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                    <Typography variant="body1" sx={{ color: textColor, mb: 1 }}>
                      Drag & drop images here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or click to select files
                    </Typography>
                    <input
                      id="file-input"
                      type="file"
                      multiple
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileSelect(e.target.files)}
                    />
                  </Box>

                  {/* Image List */}
                  <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {uploadedImages.map((image, index) => (
                      <ListItem key={image.id} sx={{ p: 1 }}>
                        <Card sx={{ width: '100%', position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="100"
                            image={image.preview}
                            alt={`Image ${index + 1}`}
                            sx={{ objectFit: 'contain' }}
                          />
                          <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                              {image.isUploading ? `Uploading... ${image.uploadProgress}%` : 'Ready'}
                            </Typography>
                            <Box>
                              {index === 0 && (
                                <Chip 
                                  label="Primary" 
                                  size="small" 
                                  color="primary" 
                                  sx={{ mr: 1 }}
                                />
                              )}
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeImage(image.id)}
                              >
                                <RemoveIcon />
                              </IconButton>
                            </Box>
                          </CardActions>
                        </Card>
                      </ListItem>
                    ))}
                  </List>

                  {/* Multiple Image URL Fields */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: textColor }}>
                      Additional Image URLs
                    </Typography>
                    {editingImageUrls.map((url, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                          label={`Image URL ${index + 1}`}
                          value={url}
                          onChange={(e) => handleEditingImageUrlChange(index, e.target.value)}
                      size="small"
                          helperText={index === 0 ? "Primary image URL" : "Additional image URL"}
                        />
                        {editingImageUrls.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeEditingImageUrl(index)}
                            sx={{ mt: 1 }}
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addEditingImageUrl}
                      sx={{ mt: 1 }}
                    >
                      Add Image URL
                    </Button>
                  </Box>
                </Grid>

                {/* Card Details */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Player Name"
                        value={editingCard.player}
                        onChange={(e) => setEditingCard({ ...editingCard, player: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Team"
                        value={editingCard.team}
                        onChange={(e) => setEditingCard({ ...editingCard, team: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Year</InputLabel>
                        <Select
                          value={editingCard.year}
                          label="Year"
                          onChange={(e) => setEditingCard({ ...editingCard, year: e.target.value as number })}
                        >
                          {yearOptions.map((year) => (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Brand</InputLabel>
                        <Select
                          value={editingCard.brand}
                          label="Brand"
                          onChange={(e) => setEditingCard({ ...editingCard, brand: e.target.value })}
                        >
                          {brandOptions.map((brand) => (
                            <MenuItem key={brand} value={brand}>
                              {brand}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Price"
                        type="number"
                        value={editingCard.price}
                        onChange={(e) => setEditingCard({ ...editingCard, price: parseFloat(e.target.value) || 0 })}
                        InputProps={{
                          startAdornment: <Typography>$</Typography>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Sport</InputLabel>
                        <Select
                          value={editingCard.sport}
                          label="Sport"
                          onChange={(e) => setEditingCard({ ...editingCard, sport: e.target.value as any })}
                        >
                          {sportOptions.map((sport) => (
                            <MenuItem key={sport} value={sport}>
                              {sport.charAt(0).toUpperCase() + sport.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Condition</InputLabel>
                        <Select
                          value={editingCard.condition}
                          label="Condition"
                          onChange={(e) => setEditingCard({ ...editingCard, condition: e.target.value })}
                        >
                          {conditionOptions.map((condition) => (
                            <MenuItem key={condition} value={condition}>
                              {condition}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Grading Company (if graded)</InputLabel>
                        <Select
                          value={editingCard.gradingCompany || ''}
                          label="Grading Company (if graded)"
                          onChange={(e) => setEditingCard({ ...editingCard, gradingCompany: e.target.value })}
                          disabled={!editingCard.isGraded}
                        >
                          {gradingCompanyOptions.map((company) => (
                            <MenuItem key={company} value={company}>
                              {company}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Grade (if graded)</InputLabel>
                        <Select
                          value={editingCard.grade || ''}
                          label="Grade (if graded)"
                          onChange={(e) => setEditingCard({ ...editingCard, grade: e.target.value })}
                          disabled={!editingCard.isGraded}
                        >
                          {gradeOptions.map((grade) => (
                            <MenuItem key={grade} value={grade}>
                              {grade}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingCard.isGraded}
                            onChange={(e) => {
                              const isGraded = e.target.checked;
                              setEditingCard({ 
                                ...editingCard, 
                                isGraded,
                                grade: isGraded ? editingCard.grade : undefined,
                                gradingCompany: isGraded ? editingCard.gradingCompany : undefined
                              });
                            }}
                          />
                        }
                        label="Is Graded"
                      />
                      {editingCard.isGraded && editingCard.gradingCompany && editingCard.grade && (
                        <Typography variant="body2" color="primary" sx={{ mt: 1, ml: 4 }}>
                          Full Grade: {editingCard.gradingCompany} {editingCard.grade}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        value={editingCard.description}
                        onChange={(e) => setEditingCard({ ...editingCard, description: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="eBay URL"
                        value={editingCard.ebayUrl}
                        onChange={(e) => setEditingCard({ ...editingCard, ebayUrl: e.target.value })}
                      />
                    </Grid>
                    {editingCard.priceChange !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Price Change %"
                          type="number"
                          value={editingCard.priceChange}
                          onChange={(e) => setEditingCard({ ...editingCard, priceChange: parseFloat(e.target.value) || 0 })}
                          InputProps={{
                            endAdornment: <Typography>%</Typography>,
                          }}
                        />
                      </Grid>
                    )}
                    {editingCard.isHot !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editingCard.isHot}
                              onChange={(e) => setEditingCard({ ...editingCard, isHot: e.target.checked })}
                            />
                          }
                          label="Is Hot Card"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={editingCard.isHomepage || false}
                            onChange={(e) => setEditingCard({ ...editingCard, isHomepage: e.target.checked })}
                          />
                        }
                        label="Homepage Card"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCard}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={uploadedImages.some(img => img.isUploading)}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Card Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCancelAdd}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon />
            Add New Card
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Image Upload Section */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" sx={{ mb: 2, color: textColor }}>
                  Card Images ({newCardImages.length})
                </Typography>
                
                {/* Drag & Drop Upload Area */}
                <Box
                  sx={{
                    border: `2px dashed ${isNewCardDragOver ? 'primary.main' : borderColor}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: isNewCardDragOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                    transition: 'all 0.2s ease',
                    mb: 2,
                    cursor: 'pointer',
                  }}
                  onDragOver={handleNewCardDragOver}
                  onDragEnter={handleNewCardDragEnter}
                  onDragLeave={handleNewCardDragLeave}
                  onDrop={handleNewCardDrop}
                  onClick={() => document.getElementById('new-card-file-input')?.click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: textColor, mb: 1 }}>
                    Drag & drop card images here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to select files
                  </Typography>
                  <input
                    id="new-card-file-input"
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleNewCardFileSelect(e.target.files)}
                  />
                </Box>

                {/* Image List */}
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {newCardImages.map((image, index) => (
                    <ListItem key={image.id} sx={{ p: 1 }}>
                      <Card sx={{ width: '100%', position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="100"
                          image={image.preview}
                          alt={`Image ${index + 1}`}
                          sx={{ objectFit: 'contain' }}
                        />
                        <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            {image.isUploading ? `Uploading... ${image.uploadProgress}%` : 'Ready'}
                          </Typography>
                          <Box>
                            {index === 0 && (
                              <Chip 
                                label="Primary" 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1 }}
                              />
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeNewCardImage(image.id)}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        </CardActions>
                      </Card>
                    </ListItem>
                  ))}
                </List>

                {/* Multiple Image URL Fields */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: textColor }}>
                    Additional Image URLs
                  </Typography>
                  {newCardImageUrls.map((url, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        label={`Image URL ${index + 1}`}
                        value={url}
                        onChange={(e) => handleNewCardImageUrlChange(index, e.target.value)}
                        size="small"
                        helperText={index === 0 ? "Primary image URL" : "Additional image URL"}
                      />
                      {newCardImageUrls.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeNewCardImageUrl(index)}
                          sx={{ mt: 1 }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addNewCardImageUrl}
                    sx={{ mt: 1 }}
                  >
                    Add Image URL
                  </Button>
                </Box>
              </Grid>

              {/* Card Details */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Player Name *"
                      value={newCard.player}
                      onChange={(e) => setNewCard(prev => ({ ...prev, player: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Team *"
                      value={newCard.team}
                      onChange={(e) => setNewCard(prev => ({ ...prev, team: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Year *</InputLabel>
                      <Select
                        value={newCard.year}
                        label="Year *"
                        onChange={(e) => setNewCard(prev => ({ ...prev, year: e.target.value as number }))}
                      >
                        {yearOptions.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Brand *</InputLabel>
                      <Select
                        value={newCard.brand}
                        label="Brand *"
                        onChange={(e) => setNewCard(prev => ({ ...prev, brand: e.target.value }))}
                      >
                        {brandOptions.map((brand) => (
                          <MenuItem key={brand} value={brand}>
                            {brand}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price *"
                      type="number"
                      value={newCard.price}
                      onChange={(e) => setNewCard(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      InputProps={{
                        startAdornment: <Typography>$</Typography>,
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Sport *</InputLabel>
                      <Select
                        value={newCard.sport}
                        label="Sport *"
                        onChange={(e) => setNewCard(prev => ({ ...prev, sport: e.target.value as any }))}
                      >
                        {sportOptions.map((sport) => (
                          <MenuItem key={sport} value={sport}>
                            {sport.charAt(0).toUpperCase() + sport.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Condition</InputLabel>
                      <Select
                        value={newCard.condition}
                        label="Condition"
                        onChange={(e) => setNewCard(prev => ({ ...prev, condition: e.target.value }))}
                      >
                        {conditionOptions.map((condition) => (
                          <MenuItem key={condition} value={condition}>
                            {condition}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Grading Company (if graded)</InputLabel>
                      <Select
                        value={newCard.gradingCompany || ''}
                        label="Grading Company (if graded)"
                        onChange={(e) => setNewCard(prev => ({ ...prev, gradingCompany: e.target.value }))}
                        disabled={!newCard.isGraded}
                      >
                        {gradingCompanyOptions.map((company) => (
                          <MenuItem key={company} value={company}>
                            {company}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Grade (if graded)</InputLabel>
                      <Select
                        value={newCard.grade || ''}
                        label="Grade (if graded)"
                        onChange={(e) => setNewCard(prev => ({ ...prev, grade: e.target.value }))}
                        disabled={!newCard.isGraded}
                      >
                        {gradeOptions.map((grade) => (
                          <MenuItem key={grade} value={grade}>
                            {grade}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newCard.isGraded}
                          onChange={(e) => {
                            const isGraded = e.target.checked;
                            setNewCard(prev => ({ 
                              ...prev, 
                              isGraded,
                              grade: isGraded ? prev.grade : undefined,
                              gradingCompany: isGraded ? prev.gradingCompany : undefined
                            }));
                          }}
                        />
                      }
                      label="Is Graded"
                    />
                    {newCard.isGraded && newCard.gradingCompany && newCard.grade && (
                      <Typography variant="body2" color="primary" sx={{ mt: 1, ml: 4 }}>
                        Full Grade: {newCard.gradingCompany} {newCard.grade}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: textColor }}>
                        Description
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={generateAIDescription}
                        disabled={isGeneratingDescription || !newCard.player || !newCard.team || !newCard.brand || !newCard.year}
                        sx={{ ml: 'auto' }}
                      >
                        {isGeneratingDescription ? 'Generating...' : 'Generate AI Description'}
                      </Button>
                    </Box>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={4}
                      value={newCard.description}
                      onChange={(e) => setNewCard(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter a detailed description of the card or use AI to generate one..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="eBay URL (Optional)"
                      value={newCard.ebayUrl}
                      onChange={(e) => setNewCard(prev => ({ ...prev, ebayUrl: e.target.value }))}
                      placeholder="https://www.ebay.com/..."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newCard.isHot || false}
                          onChange={(e) => setNewCard(prev => ({ ...prev, isHot: e.target.checked }))}
                        />
                      }
                      label="Hot Card"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newCard.isHomepage || false}
                          onChange={(e) => setNewCard(prev => ({ ...prev, isHomepage: e.target.checked }))}
                        />
                      }
                      label="Homepage Card"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAdd} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewCard}
            variant="outlined"
            startIcon={<SaveIcon />}
            disabled={newCardImages.some(img => img.isUploading)}
          >
            Save Card
          </Button>
          <Button
            onClick={handlePostListing}
            variant="contained"
            startIcon={<PostAddIcon />}
            disabled={newCardImages.some(img => img.isUploading)}
            sx={{
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
            }}
          >
            Post Listing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Cards Confirmation Dialog */}
      <Dialog
        open={showClearAllDialog}
        onClose={cancelClearAllCards}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Clear All Cards
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete <strong>ALL</strong> cards? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently remove all cards from your collection.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClearAllCards} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={confirmClearAllCards} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Clear All Cards
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Single Card Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={cancelDeleteCard}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Delete Card
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to delete <strong>"{cardToDelete?.player}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
          {cardToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight="bold">
                {cardToDelete.year} {cardToDelete.brand} {cardToDelete.player} {cardToDelete.team}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: mode === 'light' ? '#000000' : '#FFFFFF'
              }}>
                {formatPrice(cardToDelete.price)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteCard} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteCard} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete Card
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post Listing Options Dialog */}
      <Dialog
        open={showPostDialog}
        onClose={cancelPostListing}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PostAddIcon color="primary" />
            Post Card
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            Choose where you want to post this card:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={postOptions.postToCollection}
                  onChange={(e) => setPostOptions(prev => ({ 
                    ...prev, 
                    postToCollection: e.target.checked 
                  }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Post to Goat Collection
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add this card to the main Goat Collection
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={postOptions.postToHomepage}
                  onChange={(e) => setPostOptions(prev => ({ 
                    ...prev, 
                    postToHomepage: e.target.checked 
                  }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Post to Homepage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Feature this card on the main homepage
                  </Typography>
                </Box>
              }
            />
          </Box>

          {!postOptions.postToCollection && !postOptions.postToHomepage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one option to post the card.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPostListing} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={confirmPostListing} 
            variant="contained" 
            disabled={!postOptions.postToCollection && !postOptions.postToHomepage}
            startIcon={<PostAddIcon />}
            sx={{
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
            }}
          >
            Post Card
          </Button>
        </DialogActions>
      </Dialog>



      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminCardManager; 